import React, { useState } from 'react';
import { useCommon } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { RaceCardHeaderState } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/raceCardHeader/RaceCardHeader.state';
import {
    RaceHeaderDetailsData,
    RaceHeaderDetailsHeader,
    RaceHeaderWrapper,
} from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/raceCardHeader/RaceCardHeader.style';
import { RaceFiltersState } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/RaceFiiters.state';
import {
    ArticleExcerpt,
    RaceOverviewContentWrapper,
    RaceOverviewHeader,
    RaceOverviewWrapper,
    ReadMore,
} from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/RaceOverview.style';
import { WeatherIcons } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/raceCardHeader/WeatherIcons';
import { RenderStreamButton } from 'src/domains/sportsbook/webview/components/raceCard/raceStreamButton/RaceStreamButton';
import { observer } from 'src/utils/mobx-react';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { RaceMetaData } from 'src/domains/sportsbook/webview/components/raceCard/raceDetails/raceCardMeta/Meta';

const formatTextWithBoldNames = (text: string): React.ReactNode[] => {
    const regex = /\b(?!\(\d+\))(?=[A-Z']*[A-Z][A-Z0-9']*)([A-Z0-9']+(?:\s+[A-Z0-9']+)*)\b/g;

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    for (const match of text.matchAll(regex)) {
        const fullMatch = match[0];
        const offset = match.index;

        const before = text.substring(Math.max(0, offset - 1), offset);
        const after = text.substring(offset + fullMatch.length, offset + fullMatch.length + 1);
        if (before === '(' || after === ')') continue;

        if (lastIndex < offset) {
            parts.push(text.substring(lastIndex, offset));
        }

        parts.push(<strong key={`bold-${offset}`}>{fullMatch}</strong>);
        lastIndex = offset + fullMatch.length;
    }

    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }

    return parts;
};

interface RaceCardHeaderPropsType {
    bogCard: JSX.Element | null;
    hasStream: boolean;
    isFinishedRace: boolean;
    isRaceInProgress: boolean;
    race: EventModel;
    shouldShowBogForMarket: boolean;
    strategy: RaceFiltersState;
}

export const RaceCardHeader = observer(
    'RaceCardHeader',
    ({
        bogCard,
        hasStream,
        isFinishedRace,
        isRaceInProgress,
        race,
        shouldShowBogForMarket,
        strategy,
    }: RaceCardHeaderPropsType) => {
        const [state] = useState(() => new RaceCardHeaderState());
        const common = useCommon();
        const configState = ConfigComponents.get(common).config;

        const raceComment = race.feedRaceComment?.trim() ?? '';
        const raceWeather = race.feedRaceWeather?.trim() ?? '';
        const [raceHourPart, ...racePlaceParts] = race.name.split(' ');
        const racePlace = racePlaceParts.join(' ');
        const raceHour = raceHourPart ?? '';

        return (
            <RaceHeaderWrapper variant={configState.componentVariants.sportsbookVariant}>
                {configState.isRasGoldActive ? (
                    <RaceHeaderDetailsData
                        data-test='race-header-details-header'
                        isFinishedRace={isFinishedRace}
                    >
                        <WeatherIcons value={raceWeather} />

                        {raceHour !== '' && <span>{raceHour}</span>}
                        {racePlace !== '' && <span>{racePlace}</span>}
                    </RaceHeaderDetailsData>
                ) : (
                    <RaceHeaderDetailsHeader
                        data-test='race-header-details-header'
                        isFinishedRace={isFinishedRace}
                    >
                        {race.name}
                    </RaceHeaderDetailsHeader>
                )}

                {isRaceInProgress ? null : bogCard}

                <RaceMetaData
                    activeFilterId={strategy.activeFilterId}
                    race={race}
                    isFinishedRace={isFinishedRace}
                    shouldShowBogForMarket={shouldShowBogForMarket}
                    raceWeather={raceWeather}
                />

                {configState.isRasGoldActive && raceComment !== '' ? (
                    <RaceOverviewWrapper>
                        <RaceOverviewHeader data-test='race-overview-header'>Race Overview:</RaceOverviewHeader>

                        <RaceOverviewContentWrapper isExpanded={state.isExpanded}>
                            <ArticleExcerpt
                                isExpanded={state.isExpanded}
                                ref={state.setArticleRef}
                                key={state.isExpanded.toString()}
                            >
                                {formatTextWithBoldNames(raceComment)}
                            </ArticleExcerpt>

                            {state.showExpandCollapseButton && (
                                <ReadMore onClick={state.handleExpanded}>
                                    {state.isExpanded ? 'Read Less' : 'Read More'}
                                </ReadMore>
                            )}
                        </RaceOverviewContentWrapper>
                    </RaceOverviewWrapper>
                ) : null}

                {hasStream && isFinishedRace === false ? <RenderStreamButton race={race} /> : null}
            </RaceHeaderWrapper>
        );
    }
);
